/**
 * Реестр статей блога.
 *
 * Устроен как `segments.ts`: данные отдельно от вёрстки. Причина та же —
 * врать о готовности нельзя, а статус и языки статьи нужны сразу в трёх
 * местах (список, страница статьи, sitemap), и держать их в JSX означает
 * рассинхронизацию при первой же правке.
 *
 * Главное поле здесь — `locales`. Статья пишется под рынок своего языка и
 * переводится, только если тема универсальна (BLOG_CONTENT_PLAN §3):
 * «сколько стоит ИИ в месяц» нужна всем, а «клиенты пишут на трёх языках»
 * бессмысленна по-сербски, потому что сербу его язык родной.
 *
 * Отсюда правило, которое поисковик нарушить не простит: **hreflang
 * выставляется только на те локали, что перечислены в `locales`.** Ссылка
 * на непереведённую статью ведёт на 404 или, хуже, на страницу другого
 * языка — Google считает это ошибкой кластера и обесценивает весь набор
 * переводов, включая исправные.
 */

/** Локали, на которых вообще может существовать статья. */
export type PostLocale = 'en' | 'ru' | 'es' | 'sr';

/**
 * Продукт, которому принадлежит статья. Совпадает с `Product.id`.
 *
 * Домены обслуживаются одним образом, поэтому реестр физически общий:
 * пока блог был только у ingdg.com, это не было видно. Стоит подключить
 * раздел второму домену — и посетитель, пришедший за редактором PDF,
 * получает статью про помощника для кофейни, а поисковик видит один и
 * тот же текст на двух доменах, то есть дубль.
 *
 * Поле обязательное намеренно. Со значением по умолчанию новая статья
 * молча попадала бы на оба сайта, и автор узнавал бы об этом из выдачи.
 */
export type PostProduct = 'ingdg' | 'datahunter' | 'aisearch';

/**
 * Пространство переводов блога каждого продукта.
 *
 * Здесь, а не в файлах страниц: подписи переходов под статьёй берутся
 * по ключу, собранному из адреса, и next-intl на отсутствующий ключ
 * бросает исключение — то есть страница падает целиком. Пространство,
 * известное только странице, нельзя проверить тестом реестра, и первая
 * же статья продукта искала бы подписи в чужом словаре.
 */
export const BLOG_NAMESPACE: Record<PostProduct, string> = {
  ingdg: 'blog',
  datahunter: 'dh_blog',
  // Блог aisearch.ru (02.10.2026): только русская витрина, статьи под
  // запросы, где выдачу держат разборы («протокол совещания образец»,
  // «как записать созвон»), со ссылками на программы и инструменты.
  //
  // С 06.10.2026 и aisearch.tech: тот же словарь, язык и есть витрина.
  // ru читает только aisearch.ru, en/es/sr — только .tech (на .tech/ru
  // блога нет: витрина .tech на русском закрыта как дубль .ru).
  aisearch: 'ais_blog',
};

export interface Post {
  /** Часть URL: /blog/<slug>. Один и тот же на всех языках. */
  slug: string;
  /**
   * Чей это блог. Статья видна только на домене своего продукта.
   *
   * Реестр читают три места сразу: витрина, страница статьи и sitemap.
   * Пропустить фильтр хотя бы в одном — значит отдать чужую статью
   * поисковику при том, что на сайте её не видно.
   */
  product: PostProduct;
  /**
   * Языки, на которых статья реально написана.
   *
   * Не «планируется», а именно написана: список читают и hreflang, и
   * sitemap, и generateStaticParams. Лишняя локаль здесь означает 404 в
   * карте сайта.
   */
  locales: PostLocale[];
  /** Дата публикации, YYYY-MM-DD. Показывается человеку и уходит в разметку. */
  published: string;
  /**
   * Дата существенной правки. Пустая, если правок не было.
   *
   * Косметика датой не считается: `lastmod` в sitemap, меняющийся без
   * изменения содержания, Google помечает ненадёжным и перестаёт учитывать
   * (та же причина, по которой в карте нет `lastmod` у статических страниц).
   */
  updated?: string;
  /** Куда статья ведёт читателя дальше. Пустой массив недопустим. */
  leadsTo: string[];
  /**
   * Своя картинка для og:image и разметки, путь от корня сайта. Без неё
   * берётся общая картинка с заголовком (`/og?title=`).
   */
  image?: string;
  /**
   * Та же статья на другом языке, но под своим slug.
   *
   * Нужно там, где языки живут на разных доменах: у aisearch адрес
   * подбирается под запрос своего рынка, и русская версия «Will AI replace
   * humans?» лежит на aisearch.ru как /blog/zamenit-li-ii-cheloveka.
   * Связь пишется на одной стороне, вторая находит её сама
   * (`klasterStati`): две записи вручную рано или поздно разойдутся, а
   * hreflang Google засчитывает только взаимный.
   */
  perevody?: Partial<Record<PostLocale, string>>;
  /**
   * Тема — для блока «Ещё разборы» под статьёй: сначала статьи той же
   * темы, потом свежие. Без темы — просто свежие. Под статьёй про
   * голосовые WhatsApp «цифровой мозг мухи» читателю не по пути.
   */
  tema?: 'zapis' | 'ii-v-rabote' | 'ai-lab';
}

export const POSTS: Post[] = [
  {
    slug: 'skolko-stoit-ii-dlya-biznesa',
    product: 'ingdg',
    // Тема универсальная: расход на модели считается одинаково на любом
    // рынке, меняются валюта и примеры (BLOG_CONTENT_PLAN §3).
    // Испанская и сербская версии появятся вместе с текстами, а не
    // раньше: локаль без файла статьи ломает сборку и sitemap.
    locales: ['ru', 'en'],
    published: '2026-09-06',
    leadsTo: ['/pricing', '/features'],
  },
  {
    slug: 'kakie-zadachi-peredat-ii',
    product: 'ingdg',
    // Тема почти не разобрана по-сербски, и это наш рынок присутствия.
    // Но локаль появится здесь только вместе с сербским текстом: запись
    // без файла статьи ломает сборку и уводит робота на 404 из sitemap.
    locales: ['ru'],
    published: '2026-09-06',
    leadsTo: ['/solutions', '/features'],
  },
  {
    slug: 'zasto-mali-biznis-u-srbiji-ne-koristi-ai',
    product: 'ingdg',
    // Только SR, и перевода не будет: статья про сербский рынок изнутри.
    // По-русски она читалась бы как рассказ о чужой стране, а по-английски
    // не нужна никому. Это и есть смысл §3 плана: писать под рынок, а не
    // переводить один текст на четыре языка.
    locales: ['sr'],
    published: '2026-09-06',
    leadsTo: ['/solutions/coffee-shops', '/pricing'],
  },
  {
    slug: 'respuestas-automaticas-whatsapp-business',
    product: 'ingdg',
    // Только ES, и это не выбор из осторожности, а следствие данных.
    // WhatsApp Business в Испании и ЛатАм — канал по умолчанию, и спрос
    // вокруг него самый плотный из всего, что мы измеряли: у запроса
    // «respuestas automáticas whatsapp business» десять живых вариаций
    // (ejemplos, gratis, web, configurar, desactivar).
    // По-русски и по-сербски тема бессмысленна: там пишут в Telegram и Viber.
    locales: ['es'],
    published: '2026-09-07',
    leadsTo: ['/solutions/coffee-shops', '/pricing'],
  },
  {
    slug: 'baza-znaniy-dlya-personala-kofeyni',
    product: 'ingdg',
    // Сценарий HoReCa у нас пилотный, и статья ведёт прямо в него.
    locales: ['ru'],
    published: '2026-09-06',
    leadsTo: ['/solutions/coffee-shops', '/pricing'],
  },
  {
    slug: 'peredat-fail-s-telefona-na-kompyuter',
    product: 'datahunter',
    // Первая тема, где выдачу держат разборы, а не инструменты: по
    // запросу отвечают Reddit, Habr и Hi-Tech Mail, и страницей
    // приложения туда не попасть.
    //
    // Только RU, и это следствие замера, а не осторожности: английская
    // выдача по той же теме забита обзором ИИ, видеоблоками и форумами,
    // русская заметно свободнее. Английская версия появится отдельной
    // работой и вместе с текстом, а не записью в этом списке.
    locales: ['ru'],
    published: '2026-09-12',
    // Один переход, а не два: второй вёл в XNote, то есть предлагал
    // заметки человеку, пришедшему с задачей про файл.
    leadsTo: ['/app/file-transfer'],
  },
  {
    slug: 'kak-peredat-parol-bezopasno',
    product: 'datahunter',
    // Выдачу держат разборы, а не инструменты: Reddit, Habr, статьи
    // менеджеров паролей. При этом infogi.ru стоит там с описанием,
    // дословно совпадающим с XNote, — значит место берётся.
    locales: ['ru'],
    published: '2026-09-12',
    leadsTo: ['/app/xnote'],
  },
  {
    slug: 'merge-pdf-without-adobe',
    product: 'datahunter',
    // Только EN. Испанскую версию темы проверяли отдельно: там Adobe,
    // iLovePDF и Smallpdf занимают всё, включая обзор ИИ.
    locales: ['en'],
    published: '2026-09-12',
    leadsTo: ['/app/pdf-editor'],
  },
  {
    slug: 'wordpress-not-valid-json-response',
    product: 'datahunter',
    // Тема устроена иначе прочих: ищут не инструмент, а причину ошибки.
    // Разбор ведёт к редактору как к способу прочитать ответ сервера.
    locales: ['en'],
    published: '2026-09-12',
    leadsTo: ['/app/json-editor'],
  },
  {
    slug: 'kak-proverit-proxy',
    product: 'datahunter',
    // Разбор, а не инструмент. Оговорка про видимость трафика
    // обязательна: без неё статья вредна.
    locales: ['ru'],
    published: '2026-09-12',
    leadsTo: ['/app/proxy-list'],
  },
  {
    slug: 'pochemu-menya-ploho-slyshno',
    product: 'datahunter',
    // Половина спроса вокруг микрофона — про качество, а не про
    // наличие сигнала: 164 формулировки из 366 в замере #176.
    // Выдачу там держат разборы, инструментом туда не попасть.
    locales: ['ru'],
    published: '2026-09-12',
    leadsTo: ['/app/microphone-test'],
  },
  {
    slug: 'protokol-soveshchaniya-obrazec',
    product: 'aisearch',
    tema: 'zapis',
    // Спрос: «протокол совещания [образец / шаблон / пример]» — 45 подсказок
    // Яндекса и 48 Google (замер Seo-marketing, 02.10). Люди ищут бланк;
    // статья даёт его и ведёт к записи и расшифровке встречи.
    locales: ['ru'],
    published: '2026-10-02',
    leadsTo: ['/programmy/konspekt'],
  },
  {
    slug: 'kak-zapisat-sozvon-na-kompyutere',
    product: 'aisearch',
    tema: 'zapis',
    // Спрос: «как сделать запись созвона в зуме / телемост», «запись зум с
    // компьютера», «где найти запись телемоста» — десятки подсказок в обоих
    // поисковиках. Статьи ссылаются друг на друга: выкладываются вместе.
    locales: ['ru'],
    published: '2026-10-02',
    leadsTo: ['/programmy/konspekt', '/audio-to-text'],
  },
  {
    slug: 'telemost-zapis-kuda-sohranyaetsya',
    product: 'aisearch',
    tema: 'zapis',
    // Спрос (Seo-marketing, 12.10.2026): «куда сохраняется запись
    // телемоста», «где найти запись телемоста», «как открыть запись».
    // Статья выше отвечает на «как записать»; эта — на «куда делась и что
    // с ней делать», чтобы они не спорили за одни запросы. Онлайн-
    // расшифровку не обещаем: /audio-to-text принимает только audio/*, а
    // запись Телемоста — видеофайл.
    locales: ['ru'],
    published: '2026-10-12',
    leadsTo: ['/programmy/konspekt'],
  },
  {
    slug: 'golosovoe-v-tekst',
    product: 'aisearch',
    tema: 'zapis',
    // Спрос (Seo-marketing, 12.10.2026): «расшифровка голосовых», «голосовое
    // в текст ватсап / телеграм / макс». Ведёт на /audio-to-text. Пункт
    // про WhatsApp обещает opus: выпускать не раньше ingdg_com#386 и
    // AI-Tech-API#252. Раздел про бот Конспекта сверен с telegram_bot.py.
    locales: ['ru'],
    published: '2026-10-12',
    leadsTo: ['/audio-to-text'],
  },
  {
    slug: 'ii-konspekt-vstrech-bez-interneta',
    product: 'aisearch',
    tema: 'zapis',
    // Информационный спрос (Seo, 05.10.2026): «ии ассистент для встреч»,
    // «нейросеть для созвонов», «телемост конспект». Страница продукта
    // остаётся под «программа для записи и расшифровки».
    locales: ['ru'],
    published: '2026-10-05',
    leadsTo: ['/programmy/konspekt', '/audio-to-text'],
    image: '/blog/konspekt/04-itogi.webp',
  },
  {
    slug: 'zamenit-li-ii-cheloveka',
    product: 'aisearch',
    tema: 'ii-v-rabote',
    // Информационный спрос (Seo, 06.10.2026): «заменит ли ИИ человека /
    // программистов / бухгалтеров». Пара — will-ai-replace-humans на .tech.
    locales: ['ru'],
    published: '2026-10-06',
    leadsTo: ['/programmy/konspekt'],
  },
  {
    slug: 'cifrovoj-mozg-muhi',
    product: 'aisearch',
    tema: 'ai-lab',
    // Серия AI.LAB · Искусственная жизнь (Seo-marketing, 07.10.2026): цифровой мозг мухи на домашней видеокарте.
    // Статьи серии ссылаются друг на друга. На проде — по одной в неделю.
    locales: ['ru'],
    published: '2026-10-07',
    leadsTo: ['/gpt/answer'],
    image: '/blog/cifrovoj-mozg-muhi/sahar-hobotok-vypushchen.webp',
  },
  {
    slug: 'ekonomnyj-mozg-uchitsya',
    product: 'aisearch',
    tema: 'ai-lab',
    // Серия AI.LAB · Искусственная жизнь (Seo-marketing, 07.10.2026): почему экономный мозг учится лучше.
    // Статьи серии ссылаются друг на друга. На проде — по одной в неделю.
    locales: ['ru'],
    published: '2026-10-07',
    leadsTo: ['/gpt/answer'],
    image: '/blog/ekonomnyj-mozg-uchitsya/obuchenie-apl.webp',
  },
  {
    slug: 'evolyuciya-obmanyvaet-metriku',
    product: 'aisearch',
    tema: 'ai-lab',
    // Серия AI.LAB · Искусственная жизнь (Seo-marketing, 07.10.2026): эволюция обманывает метрику.
    // Статьи серии ссылаются друг на друга. На проде — по одной в неделю.
    locales: ['ru'],
    published: '2026-10-07',
    leadsTo: ['/gpt/answer'],
    image: '/blog/evolyuciya-obmanyvaet-metriku/evolyuciya-70-pokolenij.webp',
  },
  {
    slug: 'will-ai-replace-humans',
    product: 'aisearch',
    tema: 'ii-v-rabote',
    // Первая статья блога aisearch.tech (ТЗ Seo-marketing, 06.10.2026):
    // сильный спрос на en и es (will AI replace programmers / accountants,
    // la IA reemplazará…), на sr небольшой. Русская версия — на aisearch.ru.
    locales: ['en', 'es', 'sr'],
    published: '2026-10-06',
    // Диктовки на сербском нет, поэтому переход один — на Конспект.
    leadsTo: ['/programs/konspekt'],
    perevody: { ru: 'zamenit-li-ii-cheloveka' },
  },
  {
    slug: 'how-to-find-delete-duplicate-files-windows-11',
    product: 'datahunter',
    // Первая статья под программу FileHunter (ТЗ Seo-marketing, 14.10.2026):
    // спрос «find duplicate files windows 11 (free)», «duplicate file finder».
    // Встроенные способы (Проводник, PowerShell) — обязательная часть: по
    // ним ищут отдельно. ru и sr — позже, если спрос подтвердится.
    locales: ['en'],
    published: '2026-10-14',
    leadsTo: ['/programs/filehunter'],
    perevody: { es: 'como-eliminar-archivos-duplicados-windows-11' },
  },
  {
    slug: 'como-eliminar-archivos-duplicados-windows-11',
    product: 'datahunter',
    // Испанский адрес под свой спрос: «eliminar archivos duplicados windows 11
    // sin programas». Связь с английской версией записана у неё (perevody).
    locales: ['es'],
    published: '2026-10-14',
    leadsTo: ['/programs/filehunter'],
  },
];

/**
 * Статья по slug, если она есть на этом языке.
 *
 * Список принимается параметром, чтобы тест проверял ту же функцию, что
 * работает на сайте, а не свою копию правил. Копия расходится с оригиналом
 * молча, и тест начинает подтверждать несуществующее поведение.
 */
export function findPost(
  slug: string,
  locale: string,
  product: PostProduct,
  posts: readonly Post[] = POSTS,
) {
  const post = posts.find((p) => p.slug === slug && p.product === product);
  if (!post) return undefined;
  return post.locales.includes(locale as PostLocale) ? post : undefined;
}

/**
 * Статьи продукта, доступные на языке, от новых к старым.
 *
 * Продукт передаётся явно, а не берётся из окружения: один образ
 * обслуживает все домены, и переменная сборки была бы одна на всех
 * (см. `productByHost`).
 */
export function postsFor(
  product: PostProduct,
  locale: string,
  posts: readonly Post[] = POSTS,
): Post[] {
  return posts
    .filter((p) => p.product === product && p.locales.includes(locale as PostLocale))
    .sort((a, b) => b.published.localeCompare(a.published));
}

/** Сколько статей показывать в «Ещё разборы». */
export const POHOZHIH = 3;

/**
 * Похожие статьи для блока под статьёй: того же продукта и языка, сначала
 * той же темы, затем остальные; внутри — свежие первыми. Не больше
 * `skolko`: остальное — в списке блога по ссылке «Все статьи».
 */
export function pohozhieStati(
  post: Post,
  locale: string,
  skolko: number = POHOZHIH,
  posts: readonly Post[] = POSTS,
): Post[] {
  const drugie = postsFor(post.product, locale, posts).filter((p) => p.slug !== post.slug);
  const svoya = (p: Post) => (post.tema !== undefined && p.tema === post.tema ? 0 : 1);
  return [...drugie].sort((a, b) => svoya(a) - svoya(b)).slice(0, skolko);
}

/**
 * Языки, на которых у продукта есть хотя бы одна статья.
 *
 * Нужен витрине и карте сайта. Раздел, где не написано ни строчки,
 * показывает «первые разборы уже готовятся»: человеку это честно, а
 * для поисковика это страница без содержания, и звать его туда картой
 * сайта значит тратить обход на пустоту.
 */
export function localesWithPosts(
  product: PostProduct,
  posts: readonly Post[] = POSTS,
): PostLocale[] {
  const seen = new Set<PostLocale>();
  for (const post of posts) {
    if (post.product !== product) continue;
    for (const locale of post.locales) seen.add(locale);
  }
  return [...seen];
}

/** `ot` объявляет `na` своим переводом, и этот язык у `na` написан. */
function ssylaetsya(ot: Post, na: Post): boolean {
  return Object.entries(ot.perevody ?? {}).some(
    ([l, slug]) => slug === na.slug && na.locales.includes(l as PostLocale),
  );
}

/**
 * Все версии статьи: язык → slug.
 *
 * Свои языки статьи плюс переводы под другим slug — объявленные в ней
 * самой (`perevody`) и объявленные в статье, которая ссылается на неё.
 * Только статьи того же продукта и только реально написанные языки:
 * ссылка из `perevody` на ненаписанный перевод в кластер не попадает,
 * иначе hreflang вёл бы на 404.
 */
export function klasterStati(
  post: Post,
  posts: readonly Post[] = POSTS,
): Partial<Record<PostLocale, string>> {
  const svoi = posts.filter((p) => p.product === post.product);
  const gruppa = [post];
  for (const p of svoi) {
    if (p === post) continue;
    if (ssylaetsya(p, post) || ssylaetsya(post, p)) gruppa.push(p);
  }

  const klaster: Partial<Record<PostLocale, string>> = {};
  for (const p of gruppa) {
    for (const l of p.locales) klaster[l] ??= p.slug;
  }
  return klaster;
}

/** Дата последнего изменения: правка, если была, иначе публикация. */
export function lastModified(post: Post): string {
  return post.updated ?? post.published;
}
